import _asyncToGenerator from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import { useEffect } from 'react';
import { supportTranslationCountryConvertMap } from '../constants/country';
import { changeLanguage, getLanguage, getProjecti18n } from '../utils/i18nKit';
import { useJoveCountryInfo } from './useJoveCountryInfo';
import { JOVE_DEFAULT_LOCALE_COUNTRY } from './useLocaleSwitch';

var resolveLocaleByCountry = function resolveLocaleByCountry(country) {
  var _supportTranslationCo;

  if (!country) return null;
  return ((_supportTranslationCo = supportTranslationCountryConvertMap[country.toUpperCase()]) === null || _supportTranslationCo === void 0 ? void 0 : _supportTranslationCo.locale) || null;
};
/**
 * Reverse-infers a supported country code from a locale code (e.g. `pt-PT` -> `PT`).
 * Used when only the active language is known, e.g. when it came from the visitor IP
 * country or from an app-specific stored language.
 */


export var resolveCountryCodeByLocale = function resolveCountryCodeByLocale(locale) {
  if (!locale) return null;
  var countryEntries = Object.entries(supportTranslationCountryConvertMap).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 1),
        key = _ref2[0];

    return key !== 'DEFAULT';
  });
  var exactMatch = countryEntries.find(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        value = _ref4[1];

    return (value === null || value === void 0 ? void 0 : value.locale) === locale;
  });
  if (exactMatch) return exactMatch[0]; // Base-language entries (e.g. `pt`) listed among a country's supported languages

  var supportedMatch = countryEntries.find(function (_ref5) {
    var _supportLocales;

    var _ref6 = _slicedToArray(_ref5, 2),
        value = _ref6[1];

    return value === null || value === void 0 ? void 0 : (_supportLocales = value.supportLocales) === null || _supportLocales === void 0 ? void 0 : _supportLocales.includes(locale);
  });
  return (supportedMatch === null || supportedMatch === void 0 ? void 0 : supportedMatch[0]) || null;
};
/**
 * Resolves the country code behind the language currently applied on the login page, mirroring
 * the priority of `useLoginPageLocale`: valid URL country -> valid stored country -> country
 * reverse-inferred from the active i18n language -> default country.
 * Login pages persist the result when login succeeds so the post-login app (and the login page
 * itself after logout) boots in the same language the login page displayed.
 */

export var resolveLoginPageCountryCode = function resolveLoginPageCountryCode(_ref7) {
  var country = _ref7.country,
      storedCountry = _ref7.storedCountry;
  var urlCountry = country === null || country === void 0 ? void 0 : country.toUpperCase();
  if (urlCountry && supportTranslationCountryConvertMap[urlCountry]) return urlCountry;
  var normalizedStoredCountry = storedCountry === null || storedCountry === void 0 ? void 0 : storedCountry.toUpperCase();
  if (normalizedStoredCountry && supportTranslationCountryConvertMap[normalizedStoredCountry]) return normalizedStoredCountry;
  return resolveCountryCodeByLocale(getLanguage()) || JOVE_DEFAULT_LOCALE_COUNTRY;
};
/**
 * Applies the login page language following the shared priority order:
 * 1. the `country` field from the URL,
 * 2. the language already stored locally by the host app,
 * 3. the visitor country resolved from their IP (mapped to a locale).
 *
 * The first two sources resolve synchronously; the IP lookup is asynchronous and only
 * runs when neither of them produced a locale. Detection is deferred until i18n is
 * initialized because some apps (e.g. website) initialize it in a parent `_app` effect
 * that runs after this hook's own effect.
 */

export var useLoginPageLocale = function useLoginPageLocale(_ref8) {
  var country = _ref8.country,
      getStoredLocale = _ref8.getStoredLocale,
      _ref8$enabled = _ref8.enabled,
      enabled = _ref8$enabled === void 0 ? true : _ref8$enabled;

  var _useJoveCountryInfo = useJoveCountryInfo(),
      requestUserCountryCode = _useJoveCountryInfo.requestUserCountryCode;

  useEffect(function () {
    if (!enabled) return;
    var i18n = getProjecti18n();
    var cancelled = false;

    var applyLocaleAction = function applyLocaleAction() {
      // 1. URL country
      var urlLocale = resolveLocaleByCountry(country);

      if (urlLocale) {
        changeLanguage(urlLocale);
        return;
      } // 2. locally stored language


      var storedLocale = getStoredLocale === null || getStoredLocale === void 0 ? void 0 : getStoredLocale();

      if (storedLocale) {
        changeLanguage(storedLocale);
        return;
      } // 3. visitor country resolved from the IP address


      var requestLocaleByIp = /*#__PURE__*/function () {
        var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
          var country, ipLocale;
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return requestUserCountryCode();

                case 2:
                  country = _context.sent;

                  if (!cancelled) {
                    _context.next = 5;
                    break;
                  }

                  return _context.abrupt("return");

                case 5:
                  ipLocale = resolveLocaleByCountry(country) || supportTranslationCountryConvertMap.DEFAULT.locale;
                  changeLanguage(ipLocale);

                case 7:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        return function requestLocaleByIp() {
          return _ref9.apply(this, arguments);
        };
      }();

      requestLocaleByIp();
    };

    if (i18n.isInitialized) {
      applyLocaleAction();
      return function () {
        cancelled = true;
      };
    }

    var markReadyAction = function markReadyAction() {
      return applyLocaleAction();
    };

    i18n.on('initialized', markReadyAction);
    return function () {
      cancelled = true;
      i18n.off('initialized', markReadyAction);
    };
  }, [enabled, country]);
};
export default useLoginPageLocale;